import React, { useEffect, useMemo, useState } from "react";
import * as Highcharts from "highcharts";
import { commonChartOptions } from "../../utils/common";
import { PlanFactDetalization } from "../../../../../../../../store/reducers/dashboard/works/detalization";
import { XAxisOptions } from "highcharts";
import LinearChart from "../../../../../../../../components/modules/dashboards/сharts/defaultCharts/linearChart/LinearChart";
import { formatDateWithTemplate } from "@utils/format/date";
import { applyTip, wideFontSize20 } from "@utils/utils";
import Scrollbar from "../../../../../../../../utils/highchart/Scrollbar";
import { formatNumber2d } from "@utils/format/number";
import BaseChartTooltipWithHeader, {
    HeaderTypes
} from "@modules/dashboards/dashboardTooltips/TooltipVariants/BaseChartTooltipWithHeader/BaseChartTooltipWithHeader";
import { onCancelHighlightSeries, onHighlightSeries } from "@pages/dashboard/expenses/articles";
import { WeatherData } from "../../../../../../../../store/reducers/weather";
import { isEqual } from "lodash";
import { startOfDay } from "date-fns";
import ReactDOMServer from "react-dom/server";
import { WeatherIcon } from "@pages/dashboard/works/detalization/dynamics";
import { positionerWithOffset } from "@utils/highchart/tooltip";
import './DifferentiatedChart.module.scss';
import colors from "@infobis/ui-styles-utils/colors";

type DifferentiatedChartChartProps = {
    isFull: boolean
    planFactDetalization: PlanFactDetalization
    weatherAxis: XAxisOptions
    weatherByPeriod?: WeatherData[]
}

const SCROLL_THRESHOLD = 13;

const DifferentiatedChart = (props: DifferentiatedChartChartProps) => {
    const {
        isFull,
        weatherAxis,
        planFactDetalization,
        weatherByPeriod
    } = props;

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({ title: { text: '' }, credits: { enabled: false } });
    const [plotWidth, setPlotWidth] = useState(0);
    const elements = useMemo(() =>
        planFactDetalization[planFactDetalization.fact.length > planFactDetalization.plan.length ? 'fact' : 'plan'], [planFactDetalization])

    // Высчитывает смещение для labels
    const xLabelsPosition = useMemo(() => {
        const elementsCount = elements.length;

        if (elementsCount === 0 || isFull) return  0;
        return -(plotWidth / (elementsCount > SCROLL_THRESHOLD ? SCROLL_THRESHOLD : elementsCount) / 2)
    }, [plotWidth, elements])

    useEffect(() => {
        const defaultOptions: Highcharts.Options = {
            ...commonChartOptions,
            xAxis: [
                {
                    categories: elements.map((el) => {
                        return `${formatDateWithTemplate(el.date, isFull ? "DD.MM" : "HH:mm")}`
                    }),
                    labels: {
                        style: {
                            fontSize: wideFontSize20(),
                            color: colors.TYPO_SECONDARY
                        },
                        formatter: point => {
                            if (point.pos === 0 && !isFull) return ""
                            return point.value as string
                        },
                        align: 'center',
                        x: xLabelsPosition,
                        autoRotation: undefined,
                        enabled: true,
                    },
                    gridLineWidth: 1,
                    endOnTick: true,
                    max: Math.min(elements.length - 1, SCROLL_THRESHOLD),
                    startOnTick: true,
                    min: 0,
                    scrollbar: Scrollbar({ enabled: elements.length > SCROLL_THRESHOLD }),
                    lineColor: colors.BG_BORDER,
                },
                {
                    categories: elements.map((el) => `${el.date}`),
                    ...weatherAxis,
                    labels: {
                        ...weatherAxis.labels,
                        formatter: function (point) {
                            const weather: WeatherData | undefined = (weatherByPeriod || []).find(x => {
                                return isEqual(startOfDay(x.date), startOfDay(Number(point.axis.categories[point.pos])))
                            })
                            return ReactDOMServer.renderToString(<WeatherIcon data={weather}/>)
                        },
                    },
                },
            ],
            yAxis: {
                type: "datetime",
                title: {
                    text: ''
                },
                stackLabels: {
                    enabled: false,
                },
                gridLineWidth: 1,
                showEmpty: true,
                max: 1.2 * Math.max(
                    ...planFactDetalization.plan.map(el => el.cultivatedArea),
                    ...planFactDetalization.fact.map(el => el.cultivatedArea)
                ),
                labels: {
                    style: {
                        fontSize: wideFontSize20()
                    },
                    formatter: t => formatNumber2d(t.value as number)
                },
            },
            series: [
                {
                    pointWidth: 25,
                    stacking: undefined,
                    pointStart: 0,
                    type: 'column',
                    color: colors.ELEMENTS_CHARTS_13,
                    data: planFactDetalization.plan.map(s => s.cultivatedArea),
                    visible: true,
                    name: 'План'
                },
                {
                    pointWidth: 25,
                    stacking: undefined,
                    pointStart: 0,
                    type: 'column',
                    color: colors.ELEMENTS_CHARTS_18,
                    data: planFactDetalization.fact.map(s => s.cultivatedArea),
                    visible: true,
                    name: "Факт"
                }
            ],
            plotOptions: {
                column: {
                    groupPadding: 0.05,
                    grouping: true,
                    borderRadius: {
                        radius: 6,
                        where: 'end',
                        scope: 'point'
                    },
                    point: {
                        events: {
                            mouseOver: onHighlightSeries,
                            mouseOut: onCancelHighlightSeries,
                        }
                    }
                }
            },
            tooltip: applyTip((ctx) => {
                    return <BaseChartTooltipWithHeader
                        header={{ type: HeaderTypes.ndvi, title: ctx.x as string }}
                        elements={ctx.points?.map((point, id) => ({
                            color: point.color as string,
                            name: point.series.name,
                            value: `${formatNumber2d(point.y)} Га`,
                            id: `${point.series.name}-${id}`
                        })) || []}
                    />
                },
                {
                    shared: true,
                    outside: true,
                    positioner: positionerWithOffset({ x: -10, y: 0 })
                }
            ),
            chart: {
                ...commonChartOptions.chart,
                events: {
                    render: function () {
                        setPlotWidth(this.plotWidth)
                        this.xAxis[0].setExtremes(0, undefined, false)
                    }
                },
                zooming: {
                    type: undefined
                }
            }
        }

        setChartOptions(defaultOptions);

    }, [planFactDetalization, isFull, weatherAxis, elements, xLabelsPosition])

    return (
        <LinearChart
            options={chartOptions}
            isStock
        />
    )
}

export default DifferentiatedChart;